import React, { useCallback, useEffect, useState } from "react";

import CustomButton from "components/common/Button";
import InlineLoader from "./InlineLoader";
import { getBillingChargeSummary, listBillingCharges, searchBillingCustomers } from "services/billing";
import type { BsPaginated } from "services/billing";

import { downloadCsv } from "./csv";
import { useCursorList } from "./useCursorList";
import { CustomChip, DatePicker, TableComponent } from "./ui";
import FilterSelect from "./FilterSelect";
import {
  BsCharge,
  BsChargeSummaryRow,
  CHARGE_STATUS_OPTIONS,
  formatDate,
  formatMoney,
  paymentTypeLabel,
  statusColor,
  titleCase,
} from "./types";

type ChargeFilters = {
  startDate?: string;
  endDate?: string;
  status?: string;
  customerId?: string;
};

// <input type="date"> yields a Date at local midnight; widen to full-day bounds
// so "To" stays inclusive of the selected day.
const startOfDayIso = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toISOString();
const endOfDayIso = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();

// Guardrail so a huge unfiltered export can't page forever (100 × 500 = 50k rows).
const EXPORT_PAGE = 100;
const EXPORT_MAX_PAGES = 500;

const summaryColumns = [
  { id: "day", label: "Day" },
  {
    id: "status",
    label: "Status",
    render: (v: string) => <CustomChip label={titleCase(v)} size="small" color={statusColor(v)} />,
  },
  { id: "charges", label: "Charges" },
  { id: "customers", label: "Customers" },
  { id: "amountCents", label: "USD", render: (v: number) => formatMoney(v) },
];

const detailColumns = [
  { id: "customerName", label: "Customer", render: (v: string) => v || "—" },
  { id: "monolithCustomerId", label: "Monolith ID", render: (v: string) => v || "—" },
  { id: "paymentType", label: "Payment type", render: (v: string) => paymentTypeLabel(v) },
  // [PRD-3791] When the charge actually happened (Stripe/monolith time for migrated charges),
  // not the migration day.
  {
    id: "chargedAt",
    label: "Charge date",
    render: (v: string | null | undefined, row: BsCharge) => formatDate(v || row.createdAt),
  },
  { id: "amountCents", label: "Amount", render: (v: number, row: BsCharge) => formatMoney(v, row.currency) },
  {
    id: "status",
    label: "Status",
    render: (v: string) => <CustomChip label={titleCase(v)} size="small" color={statusColor(v)} />,
  },
  { id: "failureReason", label: "Failure reason", render: (v: string) => v || "—" },
];

/**
 * Date-range + status (+ optional customer) filter bar shared by both tables.
 * Resolving a customer name→id happens on Apply, so the whole bar commits at once.
 */
function ChargeFilterBar({
  showCustomer,
  onApply,
  onExport,
  exporting,
}: {
  showCustomer?: boolean;
  onApply: (f: ChargeFilters) => void;
  onExport: () => void;
  exporting: boolean;
}) {
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [status, setStatus] = useState("");
  const [customerText, setCustomerText] = useState("");
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async () => {
    setError(null);
    let customerId: string | undefined;
    const q = customerText.trim();
    if (showCustomer && q) {
      setResolving(true);
      try {
        const res = await searchBillingCustomers({ q, limit: 1 });
        const customer = res?.data?.[0];
        if (!customer) {
          setError("No customer matched that name, email or monolith ID.");
          setResolving(false);
          return;
        }
        customerId = customer.id;
      } catch {
        setError("Customer search failed.");
        setResolving(false);
        return;
      }
      setResolving(false);
    }
    onApply({
      startDate: from ? startOfDayIso(from) : undefined,
      endDate: to ? endOfDayIso(to) : undefined,
      status: status || undefined,
      customerId,
    });
  };

  const clear = () => {
    setFrom(null);
    setTo(null);
    setStatus("");
    setCustomerText("");
    setError(null);
    onApply({});
  };

  return (
    <div className="flex flex-col gap-025">
      <div className="flex gap-05 items-end" style={{ flexWrap: "wrap" }}>
        <div style={{ width: 165 }}>
          <DatePicker name="cr-from" label="From" value={from} onChange={setFrom} maxDate={to || undefined} placeholder="From" />
        </div>
        <div style={{ width: 165 }}>
          <DatePicker name="cr-to" label="To" value={to} onChange={setTo} minDate={from || undefined} placeholder="To" />
        </div>
        {showCustomer && (
          <div className="flex flex-col gap-025">
            <label className="para-body-xs-medium color-text-body-secondary" htmlFor="cr-customer">
              Customer
            </label>
            <input
              id="cr-customer"
              className="border rounded-lg px-075 py-05 para-body-m-regular"
              placeholder="Name, email or monolith ID"
              value={customerText}
              onChange={(e) => setCustomerText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") apply();
              }}
              style={{ minWidth: 200 }}
            />
          </div>
        )}
        <FilterSelect
          name={showCustomer ? "cr-detail-status" : "cr-summary-status"}
          label="Status"
          options={CHARGE_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
        />
        <CustomButton label={resolving ? "Applying…" : "Apply"} onClick={apply} disabled={resolving} />
        <CustomButton label="Clear" onClick={clear} />
        <CustomButton label={exporting ? "Preparing…" : "Download CSV"} onClick={onExport} disabled={exporting} />
      </div>
      {error && (
        <p className="para-body-xs-medium" style={{ color: "#d32f2f" }}>
          {error}
        </p>
      )}
    </div>
  );
}

export default function ChargeReportTab() {
  // --- Daily summary (not paginated) ---
  const [sumFilters, setSumFilters] = useState<ChargeFilters>({});
  const [summary, setSummary] = useState<BsChargeSummaryRow[]>([]);
  const [sumLoading, setSumLoading] = useState(true);
  const [sumError, setSumError] = useState<string | null>(null);
  const [sumExporting, setSumExporting] = useState(false);

  useEffect(() => {
    let active = true;
    setSumLoading(true);
    getBillingChargeSummary({ startDate: sumFilters.startDate, endDate: sumFilters.endDate, status: sumFilters.status })
      .then((rows) => {
        if (active) {
          setSummary((rows as BsChargeSummaryRow[]) ?? []);
          setSumError(null);
        }
      })
      .catch(() => {
        if (active) setSumError("Failed to load summary.");
      })
      .finally(() => {
        if (active) setSumLoading(false);
      });
    return () => {
      active = false;
    };
  }, [sumFilters]);

  const exportSummary = () => {
    setSumExporting(true);
    try {
      downloadCsv(
        "charge-summary.csv",
        ["day", "status", "charges", "customers", "usd"],
        summary.map((r) => [r.day, r.status, r.charges, r.customers, r.amountCents / 100])
      );
    } finally {
      setSumExporting(false);
    }
  };

  // --- Charge detail (cursor-paginated) ---
  const [detFilters, setDetFilters] = useState<ChargeFilters>({});
  const detLoad = useCallback(
    (cursor?: string) => listBillingCharges({ ...detFilters, cursor, limit: 25 }) as Promise<BsPaginated<BsCharge>>,
    [detFilters]
  );
  const { rows, hasMore, loading, loadingMore, error, loadMore } = useCursorList<BsCharge>(detLoad);
  const [detExporting, setDetExporting] = useState(false);

  const exportDetail = async () => {
    setDetExporting(true);
    try {
      const all: BsCharge[] = [];
      let cursor: string | undefined;
      let pages = 0;
      // Page through ALL matching rows so the CSV mirrors the derived spreadsheet.
      /* eslint-disable no-await-in-loop */
      do {
        const res = (await listBillingCharges({ ...detFilters, cursor, limit: EXPORT_PAGE })) as BsPaginated<BsCharge>;
        all.push(...(res?.data ?? []));
        cursor = res?.hasMore ? res?.cursor ?? undefined : undefined;
        pages += 1;
      } while (cursor && pages < EXPORT_MAX_PAGES);
      /* eslint-enable no-await-in-loop */
      downloadCsv(
        "charges-detail.csv",
        ["customer_name", "monolith_customer_id", "payment_type", "charge_date", "amount_usd", "status", "failure_reason"],
        all.map((c) => [
          c.customerName ?? "",
          c.monolithCustomerId ?? "",
          paymentTypeLabel(c.paymentType),
          c.chargedAt || c.createdAt,
          c.amountCents / 100,
          c.status,
          c.failureReason ?? "",
        ])
      );
    } finally {
      setDetExporting(false);
    }
  };

  return (
    <div className="flex flex-col" style={{ gap: 32 }}>
      <section className="flex flex-col gap-1">
        <p className="para-body-m-semibold color-text-heading-primary">Daily summary</p>
        <ChargeFilterBar onApply={setSumFilters} onExport={exportSummary} exporting={sumExporting} />
        {sumLoading ? (
          <InlineLoader />
        ) : sumError ? (
          <p className="para-body-m-regular color-text-body-secondary">{sumError}</p>
        ) : (
          <TableComponent columns={summaryColumns} data={summary} disableSort />
        )}
      </section>

      <section className="flex flex-col gap-1">
        <p className="para-body-m-semibold color-text-heading-primary">Charge detail</p>
        <ChargeFilterBar showCustomer onApply={setDetFilters} onExport={exportDetail} exporting={detExporting} />
        {loading ? (
          <InlineLoader />
        ) : error ? (
          <p className="para-body-m-regular color-text-body-secondary">{error}</p>
        ) : (
          <>
            <TableComponent columns={detailColumns} data={rows} disableSort />
            {hasMore && (
              <div className="flex justify-center mt-05">
                <CustomButton label={loadingMore ? "Loading…" : "Load more"} onClick={loadMore} disabled={loadingMore} />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
